import { BlobUploadCategory } from "@/types/enum/blobUploadCategory";
import { UploadParams } from "@/types/interfaces/uploadParams";
import { getDeleteLinkForProductBlobUrl, getSasUrlForProductFileUpload } from "./productService";
import { BlobClient, ContainerClient } from "@azure/storage-blob";
import { SasDeletePayload } from "@/types/interfaces/sasDeleteLink";
import { getDeleteLinkForOrderBlobUrl, getDeleteLinkForUserBlobUrl, getSasUrlForDomainLogoUpload, getSasUrlForOrderFileUpload } from "./sasService";

export class AzureBlobUploadService {
  public async uploadFiles({
    type,
    orderId,
    orderNumber,
    files,
    onProgress,
    signalMap
  }: UploadParams): Promise<string[]> {
    const sasData = (await this.requestSas(type, orderId, orderNumber)) as any;
    const { sasUrl, prefix } = sasData.sasUrlForOrderFileUpload!;
    const client = new ContainerClient(sasUrl);

    const blockSize = 512 * 1024;      // 512 KB
    const concurrency = 2;               // Number of parallel uploads
    const maxSingleShotSize = 4 * 1024 * 1024;  // 4 MB

    const uploadPromises = files.map(file => {
      const blobName = `${prefix}/${file.name}`;
      const blobClient = client.getBlockBlobClient(blobName);
      const ctrl = signalMap?.[file.name];

      return blobClient.uploadData(file, {
        abortSignal: ctrl?.signal,
        blockSize,
        concurrency,
        maxSingleShotSize,
        blobHTTPHeaders: { blobContentType: file.type || "application/octet-stream" },
        onProgress: ev => {
          const loaded = ev.loadedBytes ?? 0;
          const pct = file.size ? Math.round(loaded / file.size * 100) : 0;
          onProgress?.(file.name, pct);
        }
      })
        .then(() => blobClient.url);
    });

    return Promise.all(uploadPromises);
  }

  public async deleteFileByUrl(
    type: BlobUploadCategory,
    fileUrl: string
  ): Promise<boolean> {
    if (fileUrl === undefined) {
      return false;
    }
    const sasData = await this.requestDeleteUrl(type, fileUrl);
    const blob = new BlobClient(sasData.sasUrl);
    const res = await blob.deleteIfExists({ deleteSnapshots: "include" });
    return res.succeeded;
  }

  private async requestSas(type: BlobUploadCategory, orderId?: number, orderNumber?: string): Promise<SasPayload> {
    switch (type) {
      case BlobUploadCategory.Order:
        if (!orderId) {
          throw new Error("OrderId must not be empty");
        }
        if (!orderNumber) {
          throw new Error("OrderNumber must not be empty");
        }
        return await getSasUrlForOrderFileUpload(orderId, orderNumber);
      case BlobUploadCategory.Product:
        return await getSasUrlForProductFileUpload(1);
      case BlobUploadCategory.User:
        return await getSasUrlForDomainLogoUpload();
      default:
        throw new Error("Blob upload type not known");
    }
  }

  private async requestDeleteUrl(type: BlobUploadCategory, blobUrl: string): Promise<SasDeletePayload> {
    switch (type) {
      case BlobUploadCategory.Order:
        return await getDeleteLinkForOrderBlobUrl(blobUrl);
      case BlobUploadCategory.Product:
        return await getDeleteLinkForProductBlobUrl(blobUrl);
      case BlobUploadCategory.User:
        return await getDeleteLinkForUserBlobUrl(blobUrl);
      default:
        throw new Error("Blob upload type not known");
    }
  }
}

